import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ShieldAlert, Megaphone, Share2 } from "lucide-react";

const TILE_COLOR = {
  amber:   "from-amber-100 to-amber-50 text-amber-800",
  violet:  "from-violet-100 to-violet-50 text-violet-800",
  sky:     "from-sky-100 to-sky-50 text-sky-800",
  rose:    "from-rose-100 to-rose-50 text-rose-800",
};

function Tile({ count, label, to, Icon, color = "amber", testid }) {
  if (!count) return null;
  return (
    <Link
      to={to}
      className={`bg-gradient-to-br ${TILE_COLOR[color]} rounded-3xl p-4 flex items-center gap-3 ring-1 ring-white/60 hover:-translate-y-0.5 hover:shadow-md transition-all`}
      data-testid={testid}
    >
      <div className="w-11 h-11 rounded-2xl bg-white/70 flex items-center justify-center">
        <Icon className="w-5 h-5" strokeWidth={1.7} />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-heading font-bold leading-tight">{count}</div>
        <div className="text-xs font-medium opacity-80">{label}</div>
      </div>
    </Link>
  );
}

/**
 * Compact pending-acknowledgements widget for owner + branch-admin dashboards.
 * Polls every 60s; silent on error; renders nothing if there's nothing pending.
 */
export default function PendingAcksWidget() {
  const [s, setS] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/comms/pending-summary");
        if (!cancelled) setS(r.data);
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!s) return null;
  const totalOwner = (s.incidents_pending_review || 0) + (s.incidents_pending_share || 0);
  const totalStaff = (s.memos_to_ack || 0) + (s.incidents_to_ack || 0);
  if (totalOwner + totalStaff === 0) return null;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pending-acks-widget">
      {/* Owner tier */}
      <Tile
        count={s.incidents_pending_review}
        label="Incidents to review"
        to="/incidents"
        Icon={ShieldAlert}
        color="amber"
        testid="pending-incidents-review"
      />
      <Tile
        count={s.incidents_pending_share}
        label="Acknowledged — ready to share"
        to="/incidents"
        Icon={Share2}
        color="sky"
        testid="pending-incidents-share"
      />
      {/* Branch admin */}
      <Tile
        count={s.memos_to_ack}
        label="Memos to acknowledge"
        to="/memos"
        Icon={Megaphone}
        color="violet"
        testid="pending-memos-ack"
      />
      <Tile
        count={s.incidents_to_ack}
        label="Incidents to confirm"
        to="/incidents"
        Icon={ShieldAlert}
        color="rose"
        testid="pending-incidents-ack"
      />
    </div>
  );
}
